import React, { useCallback, useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AppLayout, SideNavigation, Container, Alert, TopNavigation } from '@cloudscape-design/components';
import MappingReviewTable from './components/MappingReviewTable';
import Dashboard from './components/Dashboard';
import MappingLabels from './components/MappingLabels';
import ViewMappings from './components/ViewMappings';
import MappingJobs from './components/MappingJobs';
import ControlExplorer from './components/ControlExplorer';
import FrameworkIngest from './components/FrameworkIngest';
import ProtectedRoute from './components/ProtectedRoute';
import { getUserAlias, getCleanUsername } from './auth/midway';
import { filterNavigationByBindleLocks, canAccessPage } from './auth/bindleAuthorization';

function App() {
  const [navigationOpen, setNavigationOpen] = useState(false);
  const [cleanUsername, setCleanUsername] = useState('');
  const [error, setError] = useState(null);
  const [filteredNavigationItems, setFilteredNavigationItems] = useState([]);
  const [authLoading, setAuthLoading] = useState(true);

  // Get clean username on component mount (following AWS Config Exporter pattern)
  const getUsername = useCallback(async () => {
    try {
      const username = await getCleanUsername();
      setCleanUsername(username);
    } catch (error) {
      console.error('Authentication failed:', error);
      setError('Authentication failed. Please contact your administrator for access.');
    }
  }, []);

  useEffect(() => {
    getUsername();
  }, [getUsername]);

  // Filter navigation items based on Bindle Lock permissions
  useEffect(() => {
    const filterNavigation = async () => {
      setAuthLoading(true);
      try {
        const filtered = await filterNavigationByBindleLocks(navigationItems);
        setFilteredNavigationItems(filtered);
      } catch (error) {
        console.error('Error filtering navigation:', error);
        // On error, show all navigation items
        setFilteredNavigationItems(navigationItems);
      } finally {
        setAuthLoading(false);
      }
    };

    filterNavigation();
  }, []);

  // If there's an authentication error, show error message
  if (error) {
    return (
      <Container>
        <Alert
          statusIconAriaLabel="Error"
          type="error"
          header="Authentication Error"
        >
          {error}
        </Alert>
      </Container>
    );
  }

  // Main navigation items for sidebar
  const navigationItems = [
    {
      type: 'section',
      text: 'Navigation',
      items: [
        {
          type: 'link',
          text: 'Dashboard',
          href: '/dashboard',
        },
        {
          type: 'link',
          text: 'View Mappings',
          href: '/view-mappings',
        },
        {
          type: 'link',
          text: 'Review Mappings',
          href: '/review',
        },
        {
          type: 'link',
          text: 'Community Labeling',
          href: '/labels',
        },
        {
          type: 'link',
          text: 'Mapping Jobs',
          href: '/mapping-jobs',
        },
        {
          type: 'link',
          text: 'Control Explorer',
          href: '/explorer',
        },
        {
          type: 'link',
          text: 'Ingest Framework',
          href: '/ingest',
        },
      ],
    },
    {
      type: 'section',
      text: 'Resources',
      items: [
        {
          type: 'link',
          text: 'Documentation',
          href: '#',
          external: true,
        },
        {
          type: 'link',
          text: 'Support',
          href: '#',
          external: true,
        },
        {
          type: 'link',
          text: 'Feedback',
          href: '#',
          external: true,
        },
      ],
    },
  ];

  return (
    <>
      <TopNavigation
        identity={{
          href: '/',
          title: 'Nexus - Control Navigator',
        }}
        utilities={[
          {
            type: 'button',
            text: cleanUsername || 'user',
            iconName: 'user-profile',
          },
        ]}
      />
      <AppLayout
        navigationOpen={navigationOpen}
        onNavigationChange={({ detail }) => setNavigationOpen(detail.open)}
        toolsHide={true}
        navigationWidth={280}
        navigation={
          <SideNavigation
            header={{
              text: 'Nexus',
              href: '/',
            }}
            items={authLoading ? navigationItems : filteredNavigationItems}
          />
        }
        content={
          <Container>
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute path="/dashboard">
                    <Dashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/view-mappings" 
                element={
                  <ProtectedRoute path="/view-mappings">
                    <ViewMappings />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/review" 
                element={
                  <ProtectedRoute path="/review">
                    <MappingReviewTable />
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/labels"
                element={
                  <ProtectedRoute path="/labels">
                    <MappingLabels />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mapping-jobs"
                element={
                  <ProtectedRoute path="/mapping-jobs">
                    <MappingJobs />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/explorer"
                element={
                  <ProtectedRoute path="/explorer">
                    <ControlExplorer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/ingest"
                element={
                  <ProtectedRoute path="/ingest">
                    <FrameworkIngest />
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Container>
        }
      />
    </>
  );
}

export default App;
